import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { createContext, useContext, useState } from 'react';
import { cn } from '../../lib/classNameHelpers';

type AccordionContextType = {
  type: 'single' | 'multiple';
  collapsible?: boolean;
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
};

const AccordionContext = createContext<AccordionContextType | null>(null);

function useAccordionContext() {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('AccordionItem must be used within an Accordion');
  }
  return context;
}

type AccordionItemContextType = {
  value: string;
  isOpen: boolean;
  onToggle: () => void;
  disabled?: boolean;
};

const AccordionItemContext = createContext<AccordionItemContextType | null>(null);

function useAccordionItemContext() {
  const context = useContext(AccordionItemContext);
  if (!context) {
    throw new Error('AccordionTrigger and AccordionContent must be used within an AccordionItem');
  }
  return context;
}

export type AccordionProps = {
  type: 'single' | 'multiple';
  collapsible?: boolean;
  value?: string | string[];
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  className?: string;
  children: React.ReactNode;
};

export function Accordion({
  type,
  collapsible = false,
  value: controlledValue,
  defaultValue,
  onValueChange,
  className,
  children,
}: AccordionProps) {
  const [internalValue, setInternalValue] = useState<string | string[]>(
    defaultValue || (type === 'multiple' ? [] : '')
  );

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const handleValueChange = (newValue: string | string[]) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <AccordionContext.Provider
      value={{
        type,
        collapsible,
        value: currentValue,
        onValueChange: handleValueChange,
      }}
    >
      <div className={cn('flex flex-col gap-1 px-2', className)}>{children}</div>
    </AccordionContext.Provider>
  );
}

export type AccordionItemProps = {
  value: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

export function AccordionItem({
  value,
  disabled = false,
  className,
  children,
  ...props
}: AccordionItemProps) {
  const { type, collapsible, value: currentValue, onValueChange } = useAccordionContext();

  const isOpen =
    type === 'multiple'
      ? Array.isArray(currentValue) && currentValue.includes(value)
      : currentValue === value;

  const onToggle = () => {
    if (disabled) return;

    if (type === 'multiple') {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      const newValue = isOpen ? currentArray.filter((v) => v !== value) : [...currentArray, value];
      onValueChange?.(newValue);
    } else {
      // Single mode
      const newValue = isOpen && collapsible ? '' : value;
      onValueChange?.(newValue);
    }
  };

  return (
    <AccordionItemContext.Provider value={{ value, isOpen, onToggle, disabled }}>
      <div
        className={cn('rounded-lg bg-neutral-800', className)}
        data-state={isOpen ? 'open' : 'closed'}
        {...props}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

export type AccordionTriggerProps = {
  className?: string;
  children: React.ReactNode;
  hideChevron?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export function AccordionTrigger({
  className,
  children,
  hideChevron = false,
  ...props
}: AccordionTriggerProps) {
  const { isOpen, onToggle, disabled } = useAccordionItemContext();

  return (
    <button
      type="button"
      aria-expanded={isOpen}
      disabled={disabled}
      data-state={isOpen ? 'open' : 'closed'}
      className={cn(
        'flex w-full items-center justify-between px-3 py-2 text-left',
        'rounded-lg data-[state=open]:rounded-b-none',
        'has-hover:hover:bg-backgroundHoverOverlay',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'transition-colors duration-200',
        className
      )}
      onClick={onToggle}
      {...props}
    >
      <div className="flex-1">{children}</div>
      {!hideChevron && (
        <ChevronDownIcon
          className={cn(
            'ml-2 size-5 shrink-0 text-contentSecondary transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      )}
    </button>
  );
}

export type AccordionContentProps = {
  className?: string;
  children: React.ReactNode;
} & React.ComponentProps<typeof motion.div>;

export function AccordionContent({ className, children, ...props }: AccordionContentProps) {
  const { isOpen } = useAccordionItemContext();

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          data-state={isOpen ? 'open' : 'closed'}
          className={cn('overflow-hidden px-3 pb-2', className)}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            duration: 0.2,
            ease: 'easeInOut',
          }}
          {...props}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
